// src/components/SwUpdatePrompt.jsx
//
// Listens for the service-worker "update available" custom event and
// shows a persistent snackbar with a "Reload" action. On click it tells
// the waiting SW to skip waiting; the controllerchange listener in
// registerServiceWorker.js then reloads the tab so the new bundle is
// in use.

import React, { useEffect, useRef } from 'react';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';

const UPDATE_EVENT = 'caro:sw-update-available';

const SwUpdatePrompt = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const snackKeyRef = useRef(null);

  useEffect(() => {
    const onUpdate = (e) => {
      const registration = e.detail?.registration;
      // Don't stack duplicate prompts.
      if (snackKeyRef.current) return;
      snackKeyRef.current = enqueueSnackbar('A new version of CARO is ready.', {
        variant: 'info',
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        action: (key) => (
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                // Ask the waiting worker to take over. The
                // controllerchange handler will then reload the page.
                try {
                  registration?.waiting?.postMessage?.({ type: 'SKIP_WAITING' });
                } catch {
                  /* noop */
                }
                // Fallback — if SKIP_WAITING isn't wired on the SW
                // itself, a plain reload will pick up the fresh assets.
                setTimeout(() => window.location.reload(), 300);
                closeSnackbar(key);
              }}
              sx={{ fontWeight: 700 }}
            >
              Reload
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                closeSnackbar(key);
                snackKeyRef.current = null;
              }}
            >
              Later
            </Button>
          </>
        ),
      });
    };
    window.addEventListener(UPDATE_EVENT, onUpdate);
    return () => window.removeEventListener(UPDATE_EVENT, onUpdate);
  }, [enqueueSnackbar, closeSnackbar]);

  return null;
};

export default SwUpdatePrompt;
